import { IGameSession } from "../../GameSession";
import { Resourceful, Resourceful2 } from "../../ResourceManager/Resourceful";
import { ResourceIdentifier } from "../../ResourceManager/ResourceIdentifier";
import { IReadOnlyResourceManager, IWriteOnlyResourceManager, LoadingStage, ResourceManager } from "../../ResourceManager/ResourceManager";
import { ScreenProtocolScope, ScreenProtocol } from "../../ScreenProtocol";
import { LogGroup, Rect, Scale } from "../.././MyHelpers";
import { IRunResult, BonusPickElement, BonusPickReveal, BonusRevealRemaining, RunResultMessage, BonusTotalResult, IBonusStartConfig, BonusFinish} from "../SlotGame";
import { SlotBonusParams } from "./SlotBonusParams";
import { Bonus } from "./Bonus";
import { HistoryGame, ISimpleBonusAction, PlayWindow, PublicReel, SharedJackpotWin, SimpleSlotGameState, SlotGameBet } from "../../api";
import { ActionStats } from "../IHistoyGame";
import { BonusWillUpdateFillingPotFeature } from "../FillingPot/IFillingPotIncrement";
import { FillingPotGeneric } from "../FillingPot/FillingPotGeneric";
import { ChangeSpeedParams } from "../SlotGameType1";
import { Settings } from "../../Settings";
import * as PIXI from 'pixi.js'


export type BonusFinishedCallback = () => void;
export type BonusRunFinishedCallback = (run: ISimpleBonusAction[]) => void;
export type BonusPanelRequiredCallback = (required: boolean) => void;
export const COMET_PATH = new Rect(0, 0, Scale.W(1920), Scale.H(1080));


export interface TextureDialogLanguage {
	textureBonusIntro : any;
	textureBonusFinal : any;
}


export interface ConstructReelsParams
{
	PlayWindow: PlayWindow;
	WindowRect: Rect;
	ReelSet: PublicReel[];
	Extended: boolean;
}

export abstract class SlotBonus implements Resourceful2
{
	protected readonly gameName: string = "";
	protected readonly bonusName: string = "";
	protected readonly logGroup: LogGroup;

	// resource identifier to prevent resource clash
	protected readonly parentresid: ResourceIdentifier = null;
	protected readonly resid: ResourceIdentifier = null;
	protected readonly classid: string = "";


	protected readonly screenProtocolScope: ScreenProtocolScope = null;
	protected readonly screenProtocol: ScreenProtocol = null;

	//protected abstract bonusName: string;
	protected abstract BonusName(): string;
	protected jackpotName: string = "";

	public constructor(gameName: string, params: SlotBonusParams) {

		if (Settings.Enable2plus1)
		{
			this.ScreenNumberConfiguration = 2;
		}
		this.gameName = gameName;
		this.bonusName = this.BonusName();

		//this.logGroup = gameName + "|" + bonusName;
		//this.classid = gameName + "-" + bonusName;
		//this.resid = gameName + "." + bonusName;

		this.parentresid = params.resid;
		this.resid = params.resid.Add(this.bonusName);
		this.logGroup = params.logGroup.Add(this.bonusName);

		this.screenProtocolScope = params.screenProtocolScope.Add(this.bonusName);
		this.screenProtocol = params.screenProtocol;
	}

	public Name(): string
	{
		return this.bonusName;
	}

	// spin has been pressed, either on the panel or the physical keyboard
	public abstract Spin(): void;
	public abstract Confirm(): void;
	public abstract OnPicked(pickIndex: number): void;
	// ...
	public abstract Update(): void;
	protected ScreenNumberConfiguration: number = Settings.Screens;

	// returns session of bonus
	public abstract GetSession(): IGameSession;

	public abstract ResourcesLoaded(loadingStage: LoadingStage, resourceManager: IReadOnlyResourceManager): Promise<void>;

	public abstract PrepareResources(loadingStage: LoadingStage, resourceManager: IWriteOnlyResourceManager): Promise<void>;

	public abstract UpdateSpinSpeed(changeSpeedParams: ChangeSpeedParams);
	// callbacks
	protected bonusFinishedCallbacks: BonusFinishedCallback[] = [];
	public SetBonusFinishedCallback(callback: BonusFinishedCallback): void {
		this.bonusFinishedCallbacks.push(callback);
	}

	protected OnBonusFinished(): void {
		for (let i = 0; i < this.bonusFinishedCallbacks.length; i++) {
			this.bonusFinishedCallbacks[i]();
		}
	}

	protected bonusRunFinishedCallbacks: BonusRunFinishedCallback[] = [];
	public SetBonusRunFinishedCallback(callback: BonusRunFinishedCallback): void {
		this.bonusRunFinishedCallbacks.push(callback);
	}

	protected OnBonusRunFinished(run: ISimpleBonusAction[]): void {
		for (let i = 0; i < this.bonusRunFinishedCallbacks.length; i++) {
			this.bonusRunFinishedCallbacks[i](run);
		}
	}


	protected bonusPanelRequiredCallbacks: BonusPanelRequiredCallback[] = [];
	public SetBonusPanelRequiredCallback(callback: BonusPanelRequiredCallback): void {
		this.bonusPanelRequiredCallbacks.push(callback);
	}

	protected PanelRequired(required: boolean): void {
		for (let i = 0; i < this.bonusPanelRequiredCallbacks.length; i++) {
			this.bonusPanelRequiredCallbacks[i](required);
		}
	}

	// notify bonus, that trigger animation has started
	public TriggerAnimationStartedSprite(loop : number , delay : number = 1000 ): void {}
	public TriggerAnimationStartedSpine(): void {}

	public BackGroundTransition(): void {}

	// messages from backend

	//public abstract OnBonusStartConfig(bonusConfig:BonusStartConfigMessage):void;

	// there are elements available to pick
	public abstract OnBonusPickElement(pickElement: BonusPickElement): void;

	// reveal picked element
	public abstract OnBonusPickReveal(pickReveal: BonusPickReveal): void;

	// reveal remaining elements
	public abstract OnBonusRevealRemaining(revealRemaining: BonusRevealRemaining): void;

	// another run result
	//public abstract OnBonusRunResult(runResult: IRunResult, nextBonus:SlotBonus): void;
	public abstract OnBonusRunResult(runResul?: RunResultMessage, nextBonus?:SlotBonus): void;
	

	// total result (currently sent when bonus finishes)
	public abstract OnBonusTotalResult(totalResult: BonusTotalResult): void;

	// signal, that bonus has finished
	public abstract OnBonusFinish(bonusFinish: BonusFinish): void;

	public abstract NextBonusEnd():void;

	public abstract Hide():void;
	public abstract Show():void;

	public abstract View():HTMLElement;

	public abstract Prepare(startConfig: IBonusStartConfig): void;

	public abstract Start():void;

	public abstract Reset(): void;

	public abstract ShowVideoTransition():void;

	public abstract HistoryView(actionIndex: number, gameState: SimpleSlotGameState): HTMLElement;
	public abstract HistoryStats2(actionIndex:number , gameState: SimpleSlotGameState): ActionStats;

	// public abstract PixiCanvasCrash();
	// public abstract DestroyPixi();
	// public abstract ReCreatePixiReels();

	public abstract GameSelected(app? : PIXI.Container);
	public abstract GameExit();

	public fillingPotFeature: FillingPotGeneric = null;	

	public CreateTransitionPixi(app? : PIXI.Container): void {}
	public DestroyTransition() : void {}
	public SharedJackpotWin(win: SharedJackpotWin):void {};	
	public OnResize(): void {};

	public resizeCallback: resizeGameCallback = null;

}

export class BonuRecoverStatus
{
	protected triggeredBonus: Bonus = Bonus.None;
	protected bonusFinished: boolean = false;

	
	public get TriggeredBonus() : Bonus {
		return this.triggeredBonus;
	}

	
	public get BonusFinsed() : boolean {
		return this.bonusFinished;
	}

	
	public set TriggeredBonus(v : Bonus) {
		this.triggeredBonus = v;
	}

	
	public set BonusFinsed(v : boolean) {
		this.bonusFinished = v;
	}
	
	

	
}
export type resizeGameCallback = () => void;
